/**
 * Loading and display helpers for the 项目参数 (investment program) tab.
 *
 * The service stores ratios as fractions (0.03) and amounts in USD; the table shows
 * ratios as percent and amounts in compact dollars, matching the design's
 * "比例单位 % / 年 · 金额单位 $" caption.
 */
import type {
  DiscountMethod,
  InvestmentProgramData,
  SalesRecommendationHubClient,
} from '../client/CpaClient';
import { ListInvestmentProgramsCommand } from '../client/CpaClient';

/**
 * Every program, following `nextToken` to the end. The table is small (tens of rows)
 * and the page filters, sorts and paginates client-side, so it needs the whole set.
 * `maxPages` guards against a service that keeps returning a token.
 */
export async function listAllPrograms(
  client: Pick<SalesRecommendationHubClient, 'send'>,
  maxPages = 20,
  includeDisabled = false,
): Promise<InvestmentProgramData[]> {
  const programs: InvestmentProgramData[] = [];
  let nextToken: string | undefined;
  for (let page = 0; page < maxPages; page++) {
    const out = await client.send(
      new ListInvestmentProgramsCommand({
        nextToken,
        ...(includeDisabled ? { statuses: ['active', 'disabled'] } : {}),
      }),
    );
    programs.push(...(out.programs ?? []));
    nextToken = out.nextToken;
    if (!nextToken) return programs;
  }
  throw new Error(`ListInvestmentPrograms did not finish within ${maxPages} pages`);
}

/** 0.035 → "3.5". Rounded to 2 decimals so float noise (0.07 * 100) does not show. */
export function percent(ratio: number): string {
  return String(Math.round(ratio * 10000) / 100);
}

/** 100000 → "$100K", 1500000 → "$1.5M". */
export function usd(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    // Both bounds, so the output does not depend on the Node/ICU version. With only the maximum set,
    // Node 18/22 (ICU 74/77) take the minimum as min(USD's default 2, maximum) = 1 and print `$500.0`,
    // `$100.0K`; the Brazil build's Node 24 (ICU 78) prints `$500`, `$100K`.
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(amount);
}

function range(min: string | undefined, max: string | undefined): string | null {
  if (min !== undefined && max !== undefined) return min === max ? min : `${min} - ${max}`;
  if (min !== undefined) return `≥ ${min}`;
  if (max !== undefined) return `≤ ${max}`;
  return null;
}

const opt = <T>(v: T | null | undefined, f: (v: T) => string) => (v == null ? undefined : f(v));

/** The 金额 column: the amount range in compact USD, `null` when the method has none. */
export function amountRange(method: DiscountMethod | undefined): string | null {
  if (!method) return null;
  return range(opt(method.minAmount, usd), opt(method.maxAmount, usd));
}

/** Lower-cased text the 搜索 box matches against: name, id, contacts, SLA. */
export function searchText(p: InvestmentProgramData): string {
  return [p.name, p.programId, ...(p.contacts ?? []), p.sla].filter(Boolean).join(' ').toLowerCase();
}

/** True when the error is the service refusing the caller (not a Deal Desk admin), so retrying cannot help. */
export function isAccessDenied(e: unknown): boolean {
  const err = e as {
    name?: string;
    $metadata?: { httpStatusCode?: number };
  } | null;
  return err?.name === 'AccessDeniedException' || err?.$metadata?.httpStatusCode === 403;
}

/** A contact as stored ("Laura Hao(haolaur)") split into display name and login, when it names one. */
export interface Contact {
  name: string;
  login?: string;
}

/**
 * Splits stored contact strings into people. A string may name several
 * ("A(a), B(b)"); one without a "(login)" — e.g. "Contact SCE POC" — stays text.
 */
export function parseContacts(contacts: string[] | undefined): Contact[] {
  return (contacts ?? []).flatMap((raw) => {
    const people = [...raw.matchAll(/([^(),，]*)\(\s*([a-z][a-z0-9-]*)\s*\)/gi)].map((m) => ({
      name: m[1].trim() || m[2],
      login: m[2].toLowerCase(),
    }));
    return people.length ? people : raw.trim() ? [{ name: raw.trim() }] : [];
  });
}

/**
 * Lowest and highest discount ratio of a method, or `null`. A tier table is
 * confidential, so nothing is derived from it here (not even its range).
 */
export function ratioBounds(method: DiscountMethod | undefined): { min: number; max: number } | null {
  if (!method || method.discountType === 'table') return null;
  const ratios = [method.minRatio, method.maxRatio];
  const known = ratios.filter((r): r is number => typeof r === 'number');
  return known.length ? { min: Math.min(...known), max: Math.max(...known) } : null;
}
